import { useEffect, useMemo, useState } from 'react';
import { useSetState, useUpdateEffect } from 'ahooks';
import { defineMessages } from 'react-intl';
import { getSession, isUndefined } from '@/utils/common';
import useVehicleFiledsVisiblity from '@/hooks/useVehicleFiledsVisiblity';
import * as constants from '../constants';
import * as SESSION_KEYS from '@/configs/session-storage-keys';
import type { FormInstance } from 'antd/es/form';

interface IReturn {
  paOptions: any[];
  visibilities: { [prop: string]: boolean };
  continueButtonDisalbed: boolean;
  onChangeFormItem: (field: string, value: any) => any;
  numberOfSeats: number[];
  vehicleCreateDateObj: { [prop: string]: any };
}

interface IPrams {
  (form: FormInstance, values?: any): IReturn;
}

const useFormItemReaction: IPrams = (form, values = {}) => {
  const [formValues, setFormValues] = useSetState<any>(values);
  const [paOptions, setPAOptions] = useState<any[]>([]);
  const planPicked = getSession(SESSION_KEYS.PICKED_PLAN_INFO);
  const numberOfSeats = constants.getNumberOfSearts({
    vehicleType: formValues?.vehicle_type,
    businessUsage: formValues?.business_usage,
  });
  const visibilities = useVehicleFiledsVisiblity({
    vehicleType: formValues?.vehicle_type,
    pa: formValues?.pa,
    coverageType: planPicked?.coverageType,
  });

  const {
    personalAccidentOptions: personalAccidentOptionsVisible,
    personalAccident: personalAccidentVisible,
    business: businessUsageVisible,
    tonnage: tonnageVisible,
    numberOfSeas: numberOfSeasVisible,
    physicalDamage: physicalDamageVisible,
    phsicalDamageRequired,
  } = visibilities;

  const vehicleCreateDateObj = useMemo(() => {
    let messageObject: any = {};
    switch (formValues?.vehicle_origin) {
      case 'Imported Car':
        messageObject = {
          name: 'manufacture_date',
          label: defineMessages({
            text: {
              id: 'Vehicle Manufacture Date',
            },
          }),
        };
        break;
      case 'Made in Vietnam':
      default:
        messageObject = {
          name: 'registration_date',
          label: defineMessages({
            text: {
              id: 'Vehicle Registration Date',
            },
          }),
        };
        break;
    }
    return {
      name: messageObject.name,
      label: messageObject.label['text'],
    };
  }, [formValues?.vehicle_origin]);

  const isVehicleTypeUndefined = isUndefined(formValues?.vehicle_type);
  const isBusinessUsageUndefined = isUndefined(formValues?.business_usage);
  const isNumberOfSeatsUndefined = isUndefined(formValues?.seats_number);
  const isTonnageUndefined = isUndefined(formValues?.tonnage);
  const isPhsicalDamageSectionUndefined =
    isUndefined(formValues?.vehicle_value) ||
    isUndefined(formValues?.vehicle_origin) ||
    isUndefined(formValues?.[vehicleCreateDateObj.name]);

  const continueButtonDisalbed =
    isVehicleTypeUndefined ||
    (businessUsageVisible && isBusinessUsageUndefined) ||
    (numberOfSeasVisible && isNumberOfSeatsUndefined) ||
    (tonnageVisible && isTonnageUndefined) ||
    (phsicalDamageRequired && isPhsicalDamageSectionUndefined);

  const clearFormValue = (field: string) => {
    setFormValues({ [field]: undefined });
    form.setFieldValue(['vehicleInfo', field], undefined);
  };

  const clearFormphysicalDamageValues = () => {
    clearFormValue('vehicle_value');
    clearFormValue('vehicle_origin');
    clearFormValue('manufacture_date');
    clearFormValue('registration_date');
  };

  const updatePaOptions = (value = 0) => {
    const pa_options = [];
    for (let index = 1; index <= value; index++) {
      pa_options.push({ label: index, value: index });
    }
    setPAOptions(pa_options);
  };

  const onChangeFormItem = (field: string, value: any) => {
    setFormValues({ [field]: value });
  };

  useUpdateEffect(() => {
    !businessUsageVisible && clearFormValue('business_usage');
    !numberOfSeasVisible && clearFormValue('seats_number');
    !physicalDamageVisible && clearFormphysicalDamageValues();
    !tonnageVisible && clearFormValue('tonnage');
  }, [businessUsageVisible, numberOfSeasVisible, tonnageVisible, form]);

  useUpdateEffect(() => {
    clearFormValue('seats_number');
  }, [formValues?.vehicle_type]);

  // vehicle是passenger transport时，修改business usage后，number of seats清空
  useUpdateEffect(() => {
    if (formValues?.vehicle_type !== 'Passenger Transportation') return;
    clearFormValue('seats_number');
  }, [formValues?.business_usage]);

  // 修改Vehicle Origin后，Vehicle Manufacture Date清空
  useUpdateEffect(() => {
    clearFormValue('manufacture_date');
    clearFormValue('registration_date');
  }, [formValues?.vehicle_origin]);

  // 修改Number of Seats后，PA清空, 并同步更新默认选项值
  useUpdateEffect(() => {
    clearFormValue('pa');
    clearFormValue('pa_persons_num');
    updatePaOptions(formValues?.seats_number);
  }, [formValues?.seats_number]);

  // seats_number初始如果有值，默认渲染一下选项
  useEffect(() => {
    updatePaOptions(formValues?.seats_number);
  }, [formValues?.seats_number]);

  return {
    paOptions,
    visibilities,
    continueButtonDisalbed,
    onChangeFormItem,
    numberOfSeats,
    vehicleCreateDateObj,
  };
};

export default useFormItemReaction;
